import { createFileRoute, notFound, Outlet } from "@tanstack/react-router";
import { PageSkeleton } from "@/components/common/states";
import { AppShell } from "@/components/shell/app-shell";
import { client } from "@/data";
import { qForProject } from "@/data/queries";
import { assertSurfaceAvailable } from "@/data/surfaces";

/**
 * The project layout: everything the shell needs, loaded once per project.
 *
 * This is the one place a navigation inside a project waits on data. The
 * loader resolves the project, the project list for the switcher, and the
 * accounts needing a human for the sidebar's badge; a page below it loads its
 * own. Parallel, because none of the three depends on another.
 */
export const Route = createFileRoute("/p/$project")({
  beforeLoad: ({ location }) => assertSurfaceAvailable(client.kind, location.pathname),
  loader: async ({ context, params }) => {
    const q = qForProject(params.project);
    const [projects, accountPage] = await Promise.all([
      context.queryClient.ensureQueryData(q.projects()),
      context.queryClient.ensureQueryData(q.accounts({ limit: 200 })),
      // The shell's own footer reads these; warming them here keeps the
      // sidebar from suspending on its own after the page has painted.
      context.queryClient.ensureQueryData(q.currentUser()),
      context.queryClient.ensureQueryData(q.organization()),
      ...(client.kind === "fixtures"
        ? [context.queryClient.ensureQueryData(q.billingPeriod())]
        : []),
    ]);
    const project = projects.find(
      (candidate) =>
        candidate.slug === params.project || candidate.id === params.project,
    );
    if (!project) throw notFound();
    const attention = accountPage.truncated
      ? []
      : accountPage.items.filter(
          (account) =>
            account.status === "needs_attention" || account.status === "disconnected",
        );
    return { project, projects, attention, accountsTruncated: accountPage.truncated };
  },
  pendingComponent: PageSkeleton,
  component: ProjectLayout,
});

function ProjectLayout() {
  const data = Route.useLoaderData();
  return (
    <AppShell data={data}>
      <Outlet />
    </AppShell>
  );
}
